/**
 * Clickable card wrapper used by {@link DataTable} in card view.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

/** Props for {@link TableCard}. */
interface TableCardProps {
  children: React.ReactNode;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  role?: string;
  tabIndex?: number;
  className?: string;
  ariaLabel?: string;
}

/** Card layout for a data-table record. */
export const TableCard: React.FC<TableCardProps> = ({
  children,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  className,
  ariaLabel,
}) => {
  /** Activate `onClick` on Enter/Space, then forward the original key handler. */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
    onKeyDown?.(e);
  };

  const resolvedTabIndex = onClick ? (tabIndex ?? 0) : tabIndex;

  return (
    <div
      className={cn('table-card', className)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={role || (onClick ? 'button' : undefined)}
      tabIndex={resolvedTabIndex}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
};
